import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Receipt, FileText, AlertCircle, Calendar, Search, Briefcase, DollarSign } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAX_TYPES = ["income", "sales", "payroll", "vat", "property", "excise", "other"];
const TYPE_COLOR = {
  income: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  sales: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  payroll: "bg-purple-500/10 text-purple-600 border-purple-500/30",
  vat: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  property: "bg-red-500/10 text-red-600 border-red-500/30",
  excise: "bg-pink-500/10 text-pink-600 border-pink-500/30",
  other: "bg-slate-500/10 text-slate-600 border-slate-500/30",
};

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
        <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-2xl font-bold">{value}</p></div>
      </CardContent>
    </Card>
  );
}

export default function TaxCompliance() {
  const [obligations, setObligations] = useState([]);
  const [filings, setFilings] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("obligations");
  const [search, setSearch] = useState("");

  const [oblOpen, setOblOpen] = useState(false);
  const [oblEdit, setOblEdit] = useState(null);
  const emptyObl = { name: "", tax_type: "income", jurisdiction: "", frequency: "annual", next_due_date: "", estimated_amount: "", status: "active", notes: "" };
  const [oblForm, setOblForm] = useState(emptyObl);

  const [filOpen, setFilOpen] = useState(false);
  const [filEdit, setFilEdit] = useState(null);
  const emptyFil = { obligation_id: "", tax_type: "income", jurisdiction: "", period: "", filed_date: "", due_date: "", amount: "", status: "filed", confirmation_number: "", notes: "" };
  const [filForm, setFilForm] = useState(emptyFil);

  const load = async () => {
    try {
      const [oRes, fRes, sRes] = await Promise.all([
        axios.get(`${API}/tax-compliance/obligations`),
        axios.get(`${API}/tax-compliance/filings`),
        axios.get(`${API}/tax-compliance/stats`),
      ]);
      setObligations(oRes.data || []); setFilings(fRes.data || []); setStats(sRes.data || {});
    } catch { toast.error("Failed to load tax data"); }
  };
  useEffect(() => { load(); }, []);

  const saveObl = async () => {
    if (!oblForm.name.trim() || !oblForm.jurisdiction.trim()) { toast.error("Name and Jurisdiction required"); return; }
    const payload = { ...oblForm,
      estimated_amount: oblForm.estimated_amount === "" ? null : Number(oblForm.estimated_amount),
    };
    if (!payload.next_due_date) delete payload.next_due_date;
    if (payload.estimated_amount === null) delete payload.estimated_amount;
    try {
      if (oblEdit) await axios.put(`${API}/tax-compliance/obligations/${oblEdit.id}`, payload);
      else await axios.post(`${API}/tax-compliance/obligations`, payload);
      toast.success(oblEdit ? "Obligation updated" : "Obligation added"); setOblOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delObl = async (id) => { if (!window.confirm("Delete this obligation?")) return;
    try { await axios.delete(`${API}/tax-compliance/obligations/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const saveFil = async () => {
    if (!filForm.tax_type || !filForm.jurisdiction.trim() || !filForm.period.trim()) { toast.error("Tax type, jurisdiction, and period required"); return; }
    const payload = { ...filForm,
      amount: filForm.amount === "" ? null : Number(filForm.amount),
    };
    if (!payload.obligation_id) delete payload.obligation_id;
    if (!payload.filed_date) delete payload.filed_date;
    if (!payload.due_date) delete payload.due_date;
    if (payload.amount === null) delete payload.amount;
    try {
      if (filEdit) await axios.put(`${API}/tax-compliance/filings/${filEdit.id}`, payload);
      else await axios.post(`${API}/tax-compliance/filings`, payload);
      toast.success(filEdit ? "Filing updated" : "Filing recorded"); setFilOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delFil = async (id) => { if (!window.confirm("Delete this filing?")) return;
    try { await axios.delete(`${API}/tax-compliance/filings/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const filteredObl = obligations.filter((o) => !search || `${o.name} ${o.jurisdiction} ${o.tax_type}`.toLowerCase().includes(search.toLowerCase()));
  const filteredFil = filings.filter((f) => !search || `${f.period} ${f.jurisdiction} ${f.tax_type}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="tax-compliance-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Tax &amp; Compliance</h1>
        <p className="text-sm text-muted-foreground mt-1">Track tax obligations across jurisdictions and log every filing.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Briefcase} label="Active Obligations" value={stats.active_obligations ?? 0} tint="bg-primary/10 text-primary" />
        <StatCard icon={AlertCircle} label="Overdue" value={stats.overdue ?? 0} tint="bg-red-500/10 text-red-600" />
        <StatCard icon={Calendar} label="Due in 30d" value={stats.upcoming_30d ?? 0} tint="bg-amber-500/10 text-amber-600" />
        <StatCard icon={DollarSign} label="Est. Annual" value={`$${(stats.estimated_annual_liability ?? 0).toLocaleString()}`} tint="bg-blue-500/10 text-blue-600" />
        <StatCard icon={Receipt} label="Paid YTD" value={`$${(stats.paid_ytd ?? 0).toLocaleString()}`} tint="bg-emerald-500/10 text-emerald-600" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex justify-between items-center mb-2">
          <TabsList>
            <TabsTrigger value="obligations" data-testid="tab-obligations">Obligations</TabsTrigger>
            <TabsTrigger value="filings" data-testid="tab-filings">Filings</TabsTrigger>
          </TabsList>
          <div className="flex gap-2 items-center">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" /></div>
            {tab === "obligations"
              ? <Button onClick={() => { setOblEdit(null); setOblForm(emptyObl); setOblOpen(true); }} data-testid="add-obligation-btn"><Plus className="h-4 w-4 mr-2" />New Obligation</Button>
              : <Button onClick={() => { setFilEdit(null); setFilForm(emptyFil); setFilOpen(true); }} data-testid="add-filing-btn"><Plus className="h-4 w-4 mr-2" />Record Filing</Button>}
          </div>
        </div>

        <TabsContent value="obligations">
          <Card><CardContent className="p-4">
            {filteredObl.length === 0 ? <div className="py-12 text-center text-muted-foreground"><Briefcase className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No tax obligations tracked yet.</p></div> : (
              <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Jurisdiction</TableHead><TableHead>Frequency</TableHead><TableHead>Next Due</TableHead><TableHead className="text-right">Estimated</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{filteredObl.map((o) => {
                const overdue = o.next_due_date && o.status === "active" && new Date(o.next_due_date) < new Date();
                return (
                <TableRow key={o.id} data-testid={`obligation-row-${o.id}`}>
                  <TableCell className="font-medium">{o.name}</TableCell>
                  <TableCell><Badge variant="outline" className={TYPE_COLOR[o.tax_type]}>{o.tax_type}</Badge></TableCell>
                  <TableCell className="text-sm">{o.jurisdiction}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{o.frequency}</Badge></TableCell>
                  <TableCell className="text-sm"><span className={overdue ? "text-red-600 font-medium" : ""}>{o.next_due_date ? new Date(o.next_due_date).toLocaleDateString() : "—"}</span></TableCell>
                  <TableCell className="text-right">{o.estimated_amount != null ? `$${Number(o.estimated_amount).toLocaleString()}` : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={o.status === "active" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-slate-500/10 text-slate-600 border-slate-500/30"}>{o.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setOblEdit(o); setOblForm({ ...emptyObl, ...o, estimated_amount: o.estimated_amount ?? "" }); setOblOpen(true); }} data-testid={`edit-obligation-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delObl(o.id)} data-testid={`delete-obligation-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              );})}</TableBody></Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="filings">
          <Card><CardContent className="p-4">
            {filteredFil.length === 0 ? <div className="py-12 text-center text-muted-foreground"><FileText className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No filings recorded yet.</p></div> : (
              <Table><TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Type</TableHead><TableHead>Jurisdiction</TableHead><TableHead>Filed</TableHead><TableHead>Due</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{filteredFil.map((f) => (
                <TableRow key={f.id} data-testid={`filing-row-${f.id}`}>
                  <TableCell className="font-medium">{f.period}</TableCell>
                  <TableCell><Badge variant="outline" className={TYPE_COLOR[f.tax_type]}>{f.tax_type}</Badge></TableCell>
                  <TableCell className="text-sm">{f.jurisdiction}</TableCell>
                  <TableCell className="text-sm">{f.filed_date ? new Date(f.filed_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-sm">{f.due_date ? new Date(f.due_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-right">{f.amount != null ? `$${Number(f.amount).toLocaleString()}` : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={f.status === "paid" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : f.status === "late" ? "bg-red-500/10 text-red-600 border-red-500/30" : "bg-blue-500/10 text-blue-600 border-blue-500/30"}>{f.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setFilEdit(f); setFilForm({ ...emptyFil, ...f, amount: f.amount ?? "" }); setFilOpen(true); }} data-testid={`edit-filing-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delFil(f.id)} data-testid={`delete-filing-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Obligation Dialog */}
      <Dialog open={oblOpen} onOpenChange={setOblOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{oblEdit ? "Edit Obligation" : "New Tax Obligation"}</DialogTitle><DialogDescription>Track a recurring tax obligation by jurisdiction.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Name *</Label><Input value={oblForm.name} onChange={(e) => setOblForm({ ...oblForm, name: e.target.value })} placeholder="Federal Income Tax" data-testid="obligation-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={oblForm.tax_type} onValueChange={(v) => setOblForm({ ...oblForm, tax_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TAX_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Jurisdiction *</Label><Input value={oblForm.jurisdiction} onChange={(e) => setOblForm({ ...oblForm, jurisdiction: e.target.value })} placeholder="Federal, CA, NY, etc." /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Frequency</Label>
                <Select value={oblForm.frequency} onValueChange={(v) => setOblForm({ ...oblForm, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Next Due</Label><Input type="date" value={oblForm.next_due_date || ""} onChange={(e) => setOblForm({ ...oblForm, next_due_date: e.target.value })} /></div>
              <div><Label>Estimated ($)</Label><Input type="number" step="0.01" value={oblForm.estimated_amount} onChange={(e) => setOblForm({ ...oblForm, estimated_amount: e.target.value })} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={oblForm.status} onValueChange={(v) => setOblForm({ ...oblForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={oblForm.notes || ""} onChange={(e) => setOblForm({ ...oblForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOblOpen(false)}>Cancel</Button><Button onClick={saveObl} data-testid="save-obligation-btn">{oblEdit ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filing Dialog */}
      <Dialog open={filOpen} onOpenChange={setFilOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{filEdit ? "Edit Filing" : "Record Filing"}</DialogTitle><DialogDescription>Log a tax filing with date, amount and confirmation.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Linked Obligation</Label>
              <Select value={filForm.obligation_id || "none"} onValueChange={(v) => {
                if (v === "none") { setFilForm({ ...filForm, obligation_id: "" }); return; }
                const o = obligations.find((x) => x.id === v);
                setFilForm({ ...filForm, obligation_id: v, tax_type: o?.tax_type || filForm.tax_type, jurisdiction: o?.jurisdiction || filForm.jurisdiction });
              }}>
                <SelectTrigger><SelectValue placeholder="Select obligation (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {obligations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.jurisdiction})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={filForm.tax_type} onValueChange={(v) => setFilForm({ ...filForm, tax_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TAX_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Jurisdiction *</Label><Input value={filForm.jurisdiction} onChange={(e) => setFilForm({ ...filForm, jurisdiction: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Period *</Label><Input value={filForm.period} onChange={(e) => setFilForm({ ...filForm, period: e.target.value })} placeholder="2026-Q1" /></div>
              <div><Label>Filed Date</Label><Input type="date" value={filForm.filed_date || ""} onChange={(e) => setFilForm({ ...filForm, filed_date: e.target.value })} /></div>
              <div><Label>Due Date</Label><Input type="date" value={filForm.due_date || ""} onChange={(e) => setFilForm({ ...filForm, due_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={filForm.amount} onChange={(e) => setFilForm({ ...filForm, amount: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={filForm.status} onValueChange={(v) => setFilForm({ ...filForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="filed">Filed</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="late">Late</SelectItem><SelectItem value="amended">Amended</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Confirmation #</Label><Input value={filForm.confirmation_number || ""} onChange={(e) => setFilForm({ ...filForm, confirmation_number: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={filForm.notes || ""} onChange={(e) => setFilForm({ ...filForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFilOpen(false)}>Cancel</Button><Button onClick={saveFil} data-testid="save-filing-btn">{filEdit ? "Save" : "Record"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
